import { FC, useEffect, useState } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import useStorage from "../../../hooks/useStorage";
import { route } from "../../../shim/jsbridge";
import { useNetworkNavigation } from "../../network/hooks";
import { isHash } from "../../samples_cloud/utils";
import {
  applyDatePickerFn,
  onBulkSearchFn,
  onSearchFn
} from "../../search/@types";
import SearchInput from "../../search/components/SearchInput";
import { getSearchQuery } from "../../search/components/utils";
import { BULK_SEARCH_SPLIT_REGEX, LOCATION } from "../../search/consts";
import { useGridConfig, useValidateQuery } from "../../search/hooks";
import { useSampleSummaryNavigation } from "../../search/hooks/useSampleSummaryNavigation";
import {
  constructSelections,
  formatDate,
  formatSearchQuery,
  getIsBasicNtiSearch,
  getIsSingleHashSearch
} from "../../search/util";
import { getPathParts } from "../utils";

const determineSearchInputValue = (search: string) => {
  const locationQuery = new URLSearchParams(search);
  return locationQuery.get("q") ?? "";
};

type Props = {
  setSearchBarExpanded: (value: boolean) => void;
  linksReloadDocument: boolean;
};

const GlobalSearch: FC<Props> = ({
  setSearchBarExpanded,
  linksReloadDocument
}) => {
  const { search, pathname } = useLocation() || {};
  const [searchInputValue, setSearchInputValue] = useState(
    determineSearchInputValue(search)
  );
  const [, setBulkSearch] = useStorage("bulk_search");

  const navigate = useNavigate();
  const { navigateToSampleSummary } =
    useSampleSummaryNavigation(linksReloadDocument);
  const { navigateToNti } = useNetworkNavigation(linksReloadDocument);
  const { gridConfig } = useGridConfig();

  const navigateToRoute = (routeToNavigate: string) => {
    if (linksReloadDocument) {
      window.location.href = routeToNavigate;
    } else {
      navigate(routeToNavigate);
    }
  };

  const [inputError, setInputError] = useState<string | null>(null);
  const {
    mutate: validateQuery,
    isLoading,
    shouldSwitchBulkSearchToSummary
  } = useValidateQuery(false);

  useEffect(() => {
    const [potentialHash = ""] = getPathParts(pathname);
    if (isHash(potentialHash)) {
      setSearchInputValue(potentialHash);
    } else {
      setSearchInputValue(determineSearchInputValue(search));
    }
  }, [pathname, search]);

  const handleNotFoundSearch = (bulkContent: onBulkSearchFn) => {
    setBulkSearch(bulkContent);
    navigateToRoute(
      route("search", [], {
        bulk: true,
        ...(bulkContent.reroute ? { q: bulkContent.rerouteContent } : {})
      })
    );
  };

  const handleBulkSearch = async (bulkContent: onBulkSearchFn) => {
    setBulkSearch(bulkContent);
    const hashes = bulkContent.content
      .split(BULK_SEARCH_SPLIT_REGEX)
      .filter(Boolean);
    const shouldSwitch = await shouldSwitchBulkSearchToSummary(hashes);
    if (shouldSwitch) {
      navigateToSampleSummary(hashes.at(0) as string);
      return;
    }
    navigateToRoute(
      route("search", [], {
        bulk: true,
        ...(bulkContent.reroute ? { q: bulkContent.rerouteContent } : {})
      })
    );
  };

  const handleSearch = ({ query, selections, location }: onSearchFn) => {
    setSearchInputValue(query);

    validateQuery(query, location).then(({ data: validationData }) => {
      if (validationData?.err_message) {
        setInputError(validationData?.err_message);
        return;
      }

      setSearchInputValue(validationData.transformed_query ?? "");

      const {
        is_only_hash_or_search: isOnlyHashSearch,
        transformed_query,
        requested_hashes
      } = validationData ?? {};

      if (getIsSingleHashSearch(requested_hashes)) {
        const { notfound_hashes = [] } = requested_hashes || {};
        if (notfound_hashes.length > 0) {
          handleNotFoundSearch({
            content: notfound_hashes.join(","),
            reroute: true,
            rerouteContent: transformed_query ?? ""
          });
          return;
        }

        const hash = requested_hashes?.valid_hashes?.at(0) as string;
        navigateToSampleSummary(hash);
        return;
      }

      if (getIsBasicNtiSearch(query, transformed_query ?? "")) {
        navigateToNti(transformed_query ?? "");
        return;
      }

      if (isOnlyHashSearch) {
        const { valid_hashes = [], invalid_hashes = [] } =
          requested_hashes || {};

        handleBulkSearch({
          content: valid_hashes.concat(invalid_hashes).join(","),
          reroute: true,
          rerouteContent: transformed_query ?? ""
        });
        return;
      }

      navigateToRoute(
        route("search", [], {
          q: getSearchQuery(query, transformed_query ?? ""),
          recent: true,
          selections: createSearchParams(
            constructSelections(selections)
          ).toString(),
          location:
            location && Object.values(LOCATION).includes(location)
              ? location
              : LOCATION.LOCAL
        })
      );
      setInputError(null);
    });
  };

  const handleChangeSearchInput = (value: string) => {
    const { modified } = formatSearchQuery(value);
    setSearchInputValue(modified);
  };

  const handleApplyDatePicker = (timerange: applyDatePickerFn) => {
    const query = `${searchInputValue}[${formatDate(
      timerange.start
    )} TO ${formatDate(timerange.end)}]`;
    setSearchInputValue(query);
    handleSearch({ query });
  };

  const handleSearchBarExpand = (value: boolean) => {
    // reset input error after search bar has been expanded
    if (value) {
      setInputError(null);
    }
    setSearchBarExpanded(value);
  };

  return (
    <SearchInput
      id="global_search_input"
      isLoading={isLoading}
      errorMsg={inputError}
      searchValue={searchInputValue}
      placeholder="Search Samples [Alt+S]"
      onSearch={handleSearch}
      onBulkSearch={handleBulkSearch}
      onChangeInput={handleChangeSearchInput}
      onApplyDatePicker={handleApplyDatePicker}
      setSearchBarExpanded={handleSearchBarExpand}
      gridConfig={gridConfig}
    />
  );
};

export default GlobalSearch;
